import React, { useState } from 'react'
import { AddContainer, Component4, Texts, HeaderPart, FooterPart, SaveButton, CancelButton, ThreeButtonContainer } from './Style';
import InputSelect from 'src/components/shared/Form/Select';
import theme, { palette, shadows } from 'src/config/theme';
import InputComponent from 'src/components/shared/Form/Input';
import { alpha } from '@mui/material';
import DateTimePicker from 'src/components/App/DatePicker';
import { ReactComponent as CloseIcon } from 'src/assets/images/close-circle.svg';
import { ReactComponent as AddIcon } from 'src/assets/images/add-square.svg';
import ButtonComponent from 'src/components/shared/Button';
import { postBillingRequest } from 'src/modules/app/store/appActions';
import { useDispatch } from "react-redux";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import dayjs from 'dayjs';
import useAppointmentDetails from '../../UseAppointmentDetails';
import toast from 'src/utils/toast';
import useResponsive from 'src/components/hooks/useResponsive';

const PayForOptions = {
    collect: [
        { label: 'Consultation', value: 'consultation' },
        { label: 'X-ray', value: 'x-ray' },
    ],
    refund: [
        { label: 'Refund', value: 'refund' }
    ]
};

const Pay_Method = [
    { label: 'Cash', value: 'cash' },
    { label: 'Card', value: 'card' },
    { label: 'UPI', value: 'upi' },
];
function PaymentTabForm({ onClose, fetchPayments, defaultCollectAmount = 0 }) {
    const { PatientId, user, clinicId, appointmentId } = useAppointmentDetails();
    const { isMobile, isTablet } = useResponsive();
    const dispatch = useDispatch();

    const [mode, setMode] = useState('add');
    
    const validationSchema = (mode) => {
        return Yup.object().shape({
            amount: mode === 'collect'
                ? Yup.number()
                    .required('Amount is required')
                    .min(1, 'Amount must be at least 1')
                    .max(defaultCollectAmount || undefined, defaultCollectAmount ? `Amount cannot exceed ₹${defaultCollectAmount}` : undefined)
                : Yup.number().required('Amount is required'),
            paymentFor: mode !== 'add'
                ? Yup.string().required('Payment For is required')
                : Yup.string(),
            method: (mode === 'collect' || mode === 'refund')
                ? Yup.string().required('Payment Method is required')
                : Yup.string(),
        });
    };

    const handleSubmit = async (values, { resetForm }) => {
        try {
            const normalized = { ...values };
            if (mode === 'collect' && !normalized.paymentFor) {
                normalized.paymentFor = 'consultation';
            }
            if (mode === 'collect' && Number(normalized.amount) > Number(defaultCollectAmount)) {
                toast.error(`Amount cannot exceed outstanding ₹${defaultCollectAmount}`);
                return;
            }
            const paymentData = {
                paymentFor: normalized.paymentFor,
                amount: normalized.amount,
                date: normalized.date,
                type: mode === 'collect' ? 'credit' : 'debit',
                method: mode === 'add' ? 'total' : normalized.method || '',
                clinic: clinicId,
                patient: PatientId,
                user,
                tokenAppointment: appointmentId
            };

            const response = await new Promise((resolve, reject) => {
                dispatch(postBillingRequest(paymentData, resolve, reject));
            });
            if (response?.billing?.id && fetchPayments) {
                fetchPayments();
            }


            toast.success(response.message);
            resetForm();
            onClose();
        } catch (error) {
            console.error(error.message || 'Failed to add billing');
        }
    };

    return (
        <>
            <Formik
                enableReinitialize
                key={mode}
                initialValues={{
                    paymentFor: mode === 'add' ? 'Total Entry' : mode === 'refund' ? 'refund' : '',
                    amount: mode === 'collect' ? defaultCollectAmount : mode === 'refund' ? defaultCollectAmount : '',
                    date: dayjs().format('YYYY-MM-DD'),
                    type: mode === 'collect' ? 'credit' : 'debit',
                    method: 'cash',
                }}
                validationSchema={validationSchema(mode)}
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
                    <Form>
                        <HeaderPart>
                            Add Payment
                            <CloseIcon
                                width={isMobile || isTablet ? 18 : 24}
                                height={isMobile || isTablet ? 18 : 24}
                                onClick={onClose}
                                style={{ cursor: 'pointer' }}
                            />
                        </HeaderPart>

                        <ThreeButtonContainer >
                            <ButtonComponent
                                onClick={() => {
                                    setMode('add');
                                    setFieldValue('paymentFor', 'Total Entry');
                                    setFieldValue('method', '');
                                    setFieldValue('amount', '');
                                }}
                                variant={mode === 'add' ? 'contained' : 'outlined'}
                            >
                                Add Payment Record
                            </ButtonComponent>

                            <ButtonComponent
                                onClick={() => {
                                    setMode('collect');
                                    setFieldValue('paymentFor', 'consultation');
                                    setFieldValue('method', '');
                                    setFieldValue('amount', defaultCollectAmount);
                                }}
                                variant={mode === 'collect' ? 'contained' : 'outlined'}
                            >
                                Collect Payment
                            </ButtonComponent>

                            <ButtonComponent
                                onClick={() => {
                                    setMode('refund');
                                    setFieldValue('paymentFor', 'refund');
                                    setFieldValue('method', '');
                                    setFieldValue('amount', defaultCollectAmount);
                                }}
                                variant={mode === 'refund' ? 'contained' : 'outlined'}
                            >
                                Refund Payment
                            </ButtonComponent>

                        </ThreeButtonContainer>

                        <AddContainer>
                            {mode !== 'add' && (
                                <Component4 sx={{ width: 'auto' }}>
                                    <Texts>Payment For</Texts>
                                    <InputSelect
                                        name="paymentFor"
                                        variant="outlined"
                                        value={values.paymentFor}
                                        options={PayForOptions[mode]}
                                        onChange={(value) => setFieldValue('paymentFor', value)}
                                        placeholder="Select"
                                        showNone={false}
                                        sx={{
                                            boxShadow: theme.shadows[1],
                                            '& .MuiOutlinedInput-root': {
                                                height: '52px',
                                                borderRadius: '12px',
                                            },
                                        }}
                                    />
                                </Component4>
                            )}

                            <Component4 sx={{ width: 'auto' }}>
                                <Texts>Amount</Texts>
                                <InputComponent
                                    name="amount"
                                    value={values.amount}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    variant="outlined"
                                    placeholder="Enter Amount"
                                    error={touched.amount && Boolean(errors.amount)}
                                    helperText={touched.amount && errors.amount}
                                    fullWidth
                                    sx={{
                                        backgroundColor: palette.gery.white,
                                        boxShadow: shadows[1],
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            height: '52px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            border: `1px solid ${alpha(palette.gery.black, 0.14)}`,
                                        },
                                        "& .MuiInputBase-input.MuiOutlinedInput-input": {
                                            ...(isMobile || isTablet
                                                ? theme.mobileTypography.body2
                                                : theme.typography.h5),
                                        },
                                    }}
                                />
                            </Component4>

                            <Component4 sx={{ width: 'auto' }}>
                                <Texts>Payment Date</Texts>
                                <DateTimePicker
                                    name="date"
                                    value={values.date}
                                    onChange={(val) => setFieldValue('date', val)}
                                    fullWidth
                                    type="date"
                                    disabled
                                    sx={{
                                        height: '52px',
                                        borderRadius: '12px',
                                    }}
                                />
                            </Component4>

                            {(mode === 'collect' || mode === 'refund') && (
                                <Component4 sx={{ width: 'auto' }}>
                                    <Texts>Method</Texts>
                                    <InputSelect
                                        name="method"
                                        value={values.method}
                                        options={Pay_Method}
                                        variant="outlined"
                                        onChange={(value) => setFieldValue('method', value)}
                                        placeholder="Select"
                                        showNone={false}
                                        sx={{
                                            // width: '200px',
                                            boxShadow: theme.shadows[1],
                                            '& .MuiOutlinedInput-root': {
                                                height: '52px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                // border: `1px solid ${alpha(theme.palette.gery.black, 0.14)}`,
                                                borderRadius: '12px',
                                            },
                                        }}
                                    />
                                </Component4>
                            )}
                        </AddContainer>

                        <FooterPart>
                            <ButtonComponent
                                variant="none"
                                startIcon={
                                    <CloseIcon
                                        width={isMobile || isTablet ? 18 : 24}
                                        height={isMobile || isTablet ? 18 : 24}
                                        sx={{ color: palette.primary.main }}
                                    />
                                }
                                onClick={onClose}
                            >
                                Cancel
                            </ButtonComponent>

                            <SaveButton
                                type="submit"
                                variant="outlined"
                                startIcon={
                                    <AddIcon
                                        width={isMobile || isTablet ? 18 : 24}
                                        height={isMobile || isTablet ? 18 : 24}
                                        sx={{ color: theme.palette.primary.main }}
                                    />
                                }
                            >
                                Save
                            </SaveButton>
                        </FooterPart>
                    </Form>
                )}
            </Formik>
        </>
    );
}
export default PaymentTabForm;
